import {
  ResponseDTO,
  InterviewDTO,
  PaginationDTO,
  NLSTokenDTO,
  PaginationParams,
  CursorPaginationParams,
  MessageDTO,
  CursorPaginationDTO,
  SaveMessageResponseDTO,
  CreateInterviewResponseDTO,
  InterviewPositionDTO,
} from '@/types';
import request from '@/utils/request';
import { envConfig } from '@/config/env';

export function getInterviewList(params: PaginationParams): Promise<PaginationDTO<InterviewDTO>> {
  return request
    .get<
      PaginationDTO<InterviewDTO>,
      ResponseDTO<PaginationDTO<InterviewDTO>>,
      PaginationParams
    >('/interviews', { params })
    .then((res) => res.data);
}

export function getInterview(interviewNumber: number): Promise<InterviewDTO> {
  return request
    .get<
      InterviewDTO,
      ResponseDTO<InterviewDTO>,
      { interviewNumber: number }
    >(`/interviews/${interviewNumber}`)
    .then((res) => res.data);
}

export function getMessages(
  interviewNumber: number,
  params: CursorPaginationParams,
): Promise<CursorPaginationDTO<MessageDTO>> {
  return request
    .get<
      CursorPaginationDTO<MessageDTO>,
      ResponseDTO<CursorPaginationDTO<MessageDTO>>,
      CursorPaginationParams
    >(`/interviews/${interviewNumber}/messages`, { params })
    .then((res) => res.data);
}

/**
 * 创建面试会话。
 * 无简历时传 resumeNumber: 0，需后端支持「无简历创建」语义。
 */
export function createInterview(
  position: number,
  resumeNumber?: number, // 0 表示无简历
): Promise<CreateInterviewResponseDTO> {
  return request
    .post<
      CreateInterviewResponseDTO,
      ResponseDTO<CreateInterviewResponseDTO>,
      { resumeNumber?: number; position: number }
    >('/interviews', {
      resumeNumber,
      position,
    })
    .then((res) => res.data);
}

export function getInterviewPositions(): Promise<InterviewPositionDTO[]> {
  return request
    .get<
      InterviewPositionDTO[],
      ResponseDTO<PaginationDTO<InterviewPositionDTO>>,
      void
    >('/positions')
    .then((res) => res.data.items);
}

export function startInterview(interviewNumber: number) {
  return request
    .post<InterviewDTO, ResponseDTO<InterviewDTO>, void>(`/interviews/${interviewNumber}/start`)
    .then((res) => res.data);
}

export function completeInterview(interviewNumber: number) {
  return request
    .post<
      InterviewDTO,
      ResponseDTO<{
        status: 'skipped' | 'completed';
        interviewNumber: number;
      }>,
      void
    >(`/interviews/${interviewNumber}/complete`)
    .then((res) => res.data);
}

export function createMessage(
  interviewNumber: number,
  content: string,
): Promise<SaveMessageResponseDTO> {
  return request
    .post<
      SaveMessageResponseDTO,
      ResponseDTO<SaveMessageResponseDTO>,
      { interviewNumber: number; content: string }
    >(`/interviews/messages/save`, { interviewNumber, content })
    .then((res) => res.data);
}

export interface StreamMessageOptions {
  signal?: AbortSignal;
}

/**
 * 面试下行流式消息
 * POST /api/interviews/message/stream，响应为 text/event-stream (SSE)
 * @returns 返回原生 Response，调用方通过 response.body 读取 SSE 流
 */
export function streamMessage(
  interviewNumber: number,
  messageId: string,
  options?: StreamMessageOptions,
): Promise<Response> {
  const baseURL = (envConfig.apiBaseUrl || '/api').replace(/\/$/, '');
  const url = `${baseURL}/interviews/message/stream`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = (window as unknown as { config?: { token?: string } }).config?.token;
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ interviewNumber, messageId }),
    signal: options?.signal,
  });
}

// NLS Token 缓存
let cachedToken: NLSTokenDTO | null = null;
let tokenExpireTimestamp: number = 0;

/**
 * 检查缓存的 token 是否有效（未过期）
 */
function isTokenValid(): boolean {
  if (!cachedToken || !tokenExpireTimestamp) {
    return false;
  }
  // 提前 5 分钟刷新，避免在边界时间点使用过期 token
  const bufferTime = 5 * 60 * 1000; // 5 分钟
  return Date.now() < tokenExpireTimestamp - bufferTime;
}

export function getNLSToken(): Promise<NLSTokenDTO> {
  // 如果缓存的 token 仍然有效，直接返回
  if (isTokenValid() && cachedToken) {
    cachedToken.isCached = true;
    return Promise.resolve(cachedToken);
  }

  // 重新获取 token
  return request.get<NLSTokenDTO, ResponseDTO<NLSTokenDTO>, void>('/nls/token').then((res) => {
    const tokenData = res.data;
    // 随机音色
    tokenData.voice = 'aishuo'; // Math.random() < 0.5 ? 'aida' : 'aishuo';
    // 更新缓存
    cachedToken = tokenData;
    // 根据 expireTime 或 expireIn 计算过期时间戳
    if (tokenData.expireTime) {
      tokenExpireTimestamp = new Date(tokenData.expireTime).getTime();
    }
    return tokenData;
  });
}
